import type { FormSchema, Schema } from '@cli-types/core-engine/schema.js'
import type {
  DevModeIntegrationArtifact,
  DevModeLifecycleHookExample,
  DevModeSchemaType,
  DevModeSnippet
} from '@cli-types/next-adapter/generators.js'
import { DEFAULT_ADMIN_IMPORT_ALIAS } from '@cli-constants/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { renderCreateDevModeIntegrationArtifactRenderer } from '@next-adapter/templates/renderers/generators/dev-mode-integration/create-dev-mode-integration-artifact.js'
import { createLifecycleHookImportName } from './create-lifecycle-hook-import-name.js'
import { createSchemaJsonImportPath } from './create-schema-json-import-path.js'
import { createSnippetImportName } from './create-snippet-import-name.js'
export function createDevModeIntegrationArtifact(
  schema: Schema | FormSchema,
  schemaType: DevModeSchemaType,
  adminDir: string,
  snippets: DevModeSnippet[],
  lifecycleHooks: DevModeLifecycleHookExample[] = [],
  adminImportAlias = DEFAULT_ADMIN_IMPORT_ALIAS
): DevModeIntegrationArtifact {
  const exportName = `${toCamelCase(schema.name)}DevModeIntegration`
  const loadExportName = `load${toPascalCase(schema.name)}DevModeIntegration`
  const buttonExportName = `${toPascalCase(schema.name)}DevModeIntegrateButton`
  const integrationDir = `${adminDir}/utils/dev-mode/${schema.name}`
  const schemaJsonImportPath = createSchemaJsonImportPath(schema, schemaType, adminImportAlias)
  const dynamicImportNames = [
    'schemaModule',
    ...snippets.map((snippet) => `${createSnippetImportName(snippet.id)}Module`),
    ...lifecycleHooks.map((hook) => `${createLifecycleHookImportName(hook.id)}Module`)
  ]
  const dynamicImports = [
    `    import('${schemaJsonImportPath}')`,
    ...snippets.map((snippet) => `    import('./snippets/${snippet.id}.json')`),
    ...lifecycleHooks.map((hook) => `    import('./lifecycle-hooks/${hook.id}.json')`)
  ].join(',\n')
  const snippetValues = snippets
    .map((snippet) => `    ${createSnippetImportName(snippet.id)}Module.default`)
    .join(',\n')
  const lifecycleHookValues = lifecycleHooks
    .map((hook) => `    ${createLifecycleHookImportName(hook.id)}Module.default`)
    .join(',\n')
  const integrationContent = renderCreateDevModeIntegrationArtifactRenderer({
    kind: 'renderCreateDevModeIntegrationArtifactImportsTemplate',
    props: {
      adminImportAlias: adminImportAlias,
      exportName: exportName,
      stringify: JSON.stringify(schema.name),
      stringifySerializedValue: JSON.stringify(schema.label),
      stringifyText: JSON.stringify(schemaType),
      loadExportName: loadExportName,
      join: dynamicImportNames.join(', '),
      dynamicImports: dynamicImports,
      snippetValues: snippetValues,
      lifecycleHookValues: lifecycleHookValues
    }
  })
  const buttonContent = renderCreateDevModeIntegrationArtifactRenderer({
    kind: 'renderCreateDevModeIntegrationArtifactClientModuleTemplate',
    props: {
      adminImportAlias: adminImportAlias,
      exportName: exportName,
      loadExportName: loadExportName,
      buttonExportName: buttonExportName
    }
  })

  return {
    exportName,
    importPath: `${adminImportAlias}/utils/dev-mode/${schema.name}/integration`,
    buttonExportName,
    buttonImportPath: `${adminImportAlias}/utils/dev-mode/${schema.name}/integrate-button`,
    files: [
      createGeneratedFile(`${integrationDir}/integration.ts`, integrationContent),
      createGeneratedFile(`${integrationDir}/integrate-button.tsx`, buttonContent),
      ...snippets.map((snippet) =>
        createGeneratedFile(
          `${integrationDir}/snippets/${snippet.id}.json`,
          `${JSON.stringify(snippet, null, 2)}\n`
        )
      ),
      ...lifecycleHooks.map((hook) =>
        createGeneratedFile(
          `${integrationDir}/lifecycle-hooks/${hook.id}.json`,
          `${JSON.stringify(hook, null, 2)}\n`
        )
      )
    ]
  }
}
